import { createAdminRepository } from "data/admin/repositoryFactory";
import {
  normalizeAlert,
  normalizeAppConfig,
  normalizeBooking,
  normalizeBusiness,
  normalizeDeal,
  normalizeLegalDocument,
  normalizePaymentPlan,
  normalizeProvince,
  normalizeSitePage,
  normalizeSubscription,
  normalizeSupportRequest,
  normalizeTravelBus,
  normalizeTravelCar,
  normalizeTravelFlight,
  normalizeTravelHotel,
  normalizeTravelTour,
  normalizeUserFavorites,
  normalizeUserProfile,
} from "data/admin/domain";

const provincesBase = createAdminRepository({
  collectionName: "provinces",
  idPrefix: "province",
  getId: (item) => item.id,
  normalize: normalizeProvince,
});

const dealsBase = createAdminRepository({
  collectionName: "deals",
  idPrefix: "deal",
  getId: (item) => item.id,
  normalize: normalizeDeal,
});

const businessesBase = createAdminRepository({
  collectionName: "businesses",
  idPrefix: "business",
  getId: (item) => item.id,
  normalize: normalizeBusiness,
});

const bookingsBase = createAdminRepository({
  collectionName: "bookings",
  idPrefix: "booking",
  getId: (item) => item.id,
  normalize: normalizeBooking,
});

const subscriptionsBase = createAdminRepository({
  collectionName: "subscriptions",
  idPrefix: "subscription",
  getId: (item) => item.id || item.userId,
  normalize: normalizeSubscription,
});

const usersBase = createAdminRepository({
  collectionName: "users",
  idPrefix: "user",
  getId: (item) => item.uid || item.id,
  normalize: normalizeUserProfile,
});

const supportRequestsBase = createAdminRepository({
  collectionName: "supportRequests",
  idPrefix: "support",
  getId: (item) => item.id,
  normalize: normalizeSupportRequest,
});

const paymentPlansBase = createAdminRepository({
  collectionName: "paymentPlans",
  idPrefix: "plan",
  getId: (item) => item.id,
  normalize: normalizePaymentPlan,
});

const travelFlightsBase = createAdminRepository({
  collectionName: "travelFlights",
  idPrefix: "flight",
  getId: (item) => item.id,
  normalize: normalizeTravelFlight,
});

const travelBusesBase = createAdminRepository({
  collectionName: "travelBuses",
  idPrefix: "bus",
  getId: (item) => item.id,
  normalize: normalizeTravelBus,
});

const travelHotelsBase = createAdminRepository({
  collectionName: "travelHotels",
  idPrefix: "hotel",
  getId: (item) => item.id,
  normalize: normalizeTravelHotel,
});

const travelToursBase = createAdminRepository({
  collectionName: "travelTours",
  idPrefix: "tour",
  getId: (item) => item.id,
  normalize: normalizeTravelTour,
});

const travelCarsBase = createAdminRepository({
  collectionName: "travelCars",
  idPrefix: "car",
  getId: (item) => item.id,
  normalize: normalizeTravelCar,
});

const appConfigBase = createAdminRepository({
  collectionName: "appConfig",
  idPrefix: "config",
  getId: (item) => item.id,
  normalize: normalizeAppConfig,
});

const alertsBase = createAdminRepository({
  collectionName: "alerts",
  idPrefix: "alert",
  getId: (item) => item.id,
  normalize: normalizeAlert,
});

const userFavoritesBase = createAdminRepository({
  collectionName: "userFavorites",
  idPrefix: "fav",
  getId: (item) => item.id || item.userId,
  normalize: normalizeUserFavorites,
});

const legalDocumentsBase = createAdminRepository({
  collectionName: "legalDocuments",
  idPrefix: "legal",
  getId: (item) => item.id || item.slug,
  normalize: normalizeLegalDocument,
});

const sitePagesBase = createAdminRepository({
  collectionName: "sitePages",
  idPrefix: "page",
  getId: (item) => item.id || item.slug,
  normalize: normalizeSitePage,
});

export const adminRepositories = {
  provinces: {
    ...provincesBase,
  },
  deals: {
    ...dealsBase,
  },
  businesses: {
    ...businessesBase,
    list(params = {}) {
      return businessesBase.list({
        ...params,
        filter: (item) => {
          if (params.provinceId && item.location?.provinceId !== params.provinceId) return false;
          if (params.category && item.category !== params.category) return false;
          return true;
        },
      });
    },
    listByProvince(provinceId) {
      return this.list({ provinceId });
    },
  },
  bookings: {
    ...bookingsBase,
    listByBusinessId(businessId) {
      return bookingsBase.list({ filter: (item) => item.businessId === businessId });
    },
    updateStatus(id, status) {
      return bookingsBase.update(id, { status });
    },
  },
  subscriptions: {
    ...subscriptionsBase,
  },
  users: {
    ...usersBase,
  },
  supportRequests: {
    ...supportRequestsBase,
    updateStatus(id, status) {
      return supportRequestsBase.update(id, { status });
    },
  },
  paymentPlans: {
    ...paymentPlansBase,
  },
  travelFlights: {
    ...travelFlightsBase,
  },
  travelBuses: {
    ...travelBusesBase,
  },
  travelHotels: {
    ...travelHotelsBase,
  },
  travelTours: {
    ...travelToursBase,
  },
  travelCars: {
    ...travelCarsBase,
  },
  appConfig: {
    ...appConfigBase,
  },
  alerts: {
    ...alertsBase,
  },
  userFavorites: {
    ...userFavoritesBase,
  },
  legalDocuments: {
    ...legalDocumentsBase,
  },
  sitePages: {
    ...sitePagesBase,
  },
};
